import { useRef, useState } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';
import { HoverCascader, useClickOutside, cascControl, type CascOpt } from '../../components/HoverCascader';

/**
 * Tailwind-only filter bar for the Territory Coverage page (no AntD): code/name search · a hover-expand
 * Territory cascader (shared HoverCascader) · a searchable Sales Head / Person combobox.
 */
export interface PersonOpt { value: string; name: string; role: 'SH' | 'SP' }

/** Searchable person combobox with an SH/SP role chip. */
function PersonSelect({ options, value, onChange }: { options: PersonOpt[]; value?: string; onChange: (v?: string) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => { setOpen(false); setQ(''); });
  const sel = options.find((o) => o.value === value);
  const filtered = q ? options.filter((o) => o.name.toLowerCase().includes(q.toLowerCase())) : options;
  const roleChip = (role: 'SH' | 'SP') =>
    role === 'SH' ? 'bg-sky-500/15 text-sky-600' : 'bg-brand-500/15 text-brand-700 dark:text-brand-300';

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(!open)} className={cascControl(open)} style={{ minWidth: 188 }}>
        <span className={`flex-1 truncate text-left ${sel ? 'text-content' : 'text-subtle'}`}>{sel ? sel.name : 'Sales Head / Person'}</span>
        {sel && (
          <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); onChange(undefined); }}
            className="text-subtle hover:text-content"><X size={14} /></span>
        )}
        <ChevronDown size={16} className={`shrink-0 text-subtle transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-64 overflow-hidden rounded-card border border-line bg-surface shadow-pop animate-pop-in">
          <div className="border-b border-line p-2">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search staff…" aria-label="Search staff"
              className="h-8 w-full rounded-control border border-line bg-surface px-2 text-sm text-content placeholder:text-subtle outline-none focus:border-brand-500" />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && <li className="px-3 py-2 text-sm text-subtle">No staff found</li>}
            {filtered.map((o) => (
              <li key={o.value}>
                <button type="button" onClick={() => { onChange(o.value); setOpen(false); setQ(''); }}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition hover:bg-surface-2 ${o.value === value ? 'bg-brand-50 dark:bg-brand-900/30' : ''}`}>
                  <span className="flex-1 truncate text-content">{o.name}</span>
                  <span className={`rounded-pill px-1.5 py-0.5 type-caption-strong ${roleChip(o.role)}`}>{o.role}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface Props {
  search: string; setSearch: (v: string) => void;
  scopePath?: string[]; setScopePath: (v?: string[]) => void; terrOptions: CascOpt[];
  personId?: string; setPersonId: (v?: string) => void; personOptions: PersonOpt[];
}

export function CoverageFilters({ search, setSearch, scopePath, setScopePath, terrOptions, personId, setPersonId, personOptions }: Props) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-subtle" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code or name" aria-label="Search code or name"
          className="h-9 w-52 rounded-control border border-line bg-surface pl-8 pr-7 text-sm text-content placeholder:text-subtle outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/25" />
        {search && (
          <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-subtle hover:text-content"><X size={14} /></button>
        )}
      </div>

      <HoverCascader options={terrOptions} value={scopePath} onChange={setScopePath} placeholder="State / District / Beat" />
      <PersonSelect options={personOptions} value={personId} onChange={setPersonId} />
      {/* A "Needs owner" toggle used to sit here. It was removed once the worklist card above the tree
          started listing every gap with its own Assign button: the card answers the same question more
          completely and acts on it, while the toggle only hid covered branches — invisible work on a
          tree that is mostly gaps, so it read as a control that did nothing. */}
    </div>
  );
}
